import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { postsAPI } from "../../services/api";
import { UserContext } from "../context/user/UserContext";
import Loader from "../Loader";
import Toast from "../Toast";
import styles from "../../styles/PostDetail.module.css";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Icon } from "leaflet";

// Custom marker icon
const customIcon = new Icon({
  iconUrl: "https://cdn-icons-png.flaticon.com/512/447/447031.png",
  iconSize: [38, 38],
});

const PostDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userData } = useContext(UserContext);
  const token = userData?.token;

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const data = await postsAPI.getPostById(token, id);
        setPost(data);
      } catch (error) {
        console.error("Error fetching post:", error);
        setToast({ message: `Failed to load post: ${error.message}`, type: 'error' });
      } finally {
        setLoading(false);
      }
    };

    if (token && id) {
      fetchPost();
    }
  }, [token, id]);

  if (loading) {
    return <Loader />;
  }

  if (!post) {
    return (
      <div className={styles.container}>
        <h2>Post not found</h2>
        <button onClick={() => navigate('/posts')} className={styles.backButton}>
          ← Back to Posts
        </button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <button onClick={() => navigate('/posts')} className={styles.backButton}>
        ← Back to Posts
      </button>

      <div className={styles.header}>
        <h1 className={styles.title}>{post.title}</h1>
        <span className={styles.category}>{post.category}</span>
      </div>

      <div className={styles.content}>
        <div className={styles.infoSection}>
          <h3>Location Information</h3>
          
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <strong>📍 Category:</strong>
              <span>{post.category}</span>
            </div>
            
            <div className={styles.infoItem}>
              <strong>📐 Coordinates:</strong>
              <span>{post.latitude?.toFixed(6)}, {post.longitude?.toFixed(6)}</span>
            </div>
            
            <div className={styles.infoItem}>
              <strong>👤 Posted By:</strong>
              <span>{post.profile?.username || post.username || 'Unknown'}</span>
            </div>
            
            <div className={styles.infoItem}>
              <strong>📅 Created:</strong>
              <span>{new Date(post.created_at).toLocaleDateString()}</span>
            </div>
            
            <div className={styles.infoItem}>
              <strong>✅ Verified:</strong>
              <span className={post.isverified ? styles.verified : styles.unverified}>
                {post.isverified ? '✓ Yes' : '✗ No'}
              </span>
            </div>
            
            <div className={styles.infoItem}>
              <strong>⚠️ Reported:</strong>
              <span className={post.is_reported ? styles.reported : styles.notReported}>
                {post.is_reported ? '⚠️ Yes' : '✓ No'}
              </span>
            </div>
          </div>

          <div className={styles.descriptionSection}>
            <h3>Description</h3>
            <p className={styles.description}>{post.description}</p>
          </div>

          {post.report_reasons && post.report_reasons.length > 0 && (
            <div className={styles.reportSection}>
              <h3>⚠️ Report Reasons</h3>
              <ul className={styles.reportReasons}>
                {post.report_reasons.map((reason, index) => (
                  <li key={index}>{reason}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className={styles.mapSection}>
          <h3>Map Location</h3>
          {post.latitude && post.longitude && (
            <MapContainer
              center={[post.latitude, post.longitude]}
              zoom={13}
              style={{ height: '400px', width: '100%', borderRadius: '12px' }}
            >
              <TileLayer
                attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <Marker position={[post.latitude, post.longitude]} icon={customIcon}>
                <Popup>
                  <strong>{post.title}</strong><br />
                  {post.category}
                </Popup>
              </Marker>
            </MapContainer>
          )}
        </div>
      </div>

      <div className={styles.actions}>
        <button 
          onClick={() => navigate(`/posts`)} 
          className={styles.editButton}
        >
          ← Back to All Posts
        </button>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default PostDetail;
